// The in-memory API's reporting duties (AGENT-REPORTING; 13 §Reporting duties and review of
// agent-reported numbers, 05 §Agent scopes; founder-approved 2026-10-21): `reporting.seat` (Setup ›
// KPI reporting in one call), `reporting.putDuty` / `reporting.deleteDuty` and `seats.putKpiScopes`,
// plus what the seat scorecards carry (`reporting`, `pending_review`, `last_report`) and the
// recording of a decided `kpi_value` approval. The due-rule math is domain-kpi's (`dutyStatus`,
// `describeDueRule`, `dutyOrderText`), so the twin and the API cannot disagree on Next due / Late.
//
// Rules kept from the spec:
// - Blocked (never reminds, never late): an open seat; a metric not measured on the seat or
//   without a cadence; `agent_tool` without a DE holder, without `kpis:write` in the seat's
//   effective scopes, or on a derived (formula) metric; `manual`/`import` on an event or formula
//   metric; `events` on a metric that is not event-sourced.
// - A period counts as reported with a live value or a value pending review.
// - `order_text` only for a DE-held seat with a duty; `can_edit` for managers.
// - `seat.settings.kpi_scopes`: null = the default (both scopes); `[]` = the KPI tools are off.
// - Approving a `kpi_value` records the agent's value; editing records the supervisor's value with
//   the agent's kept as `original_value`; rejecting records nothing.
//
// Seed (Acme): Riley's "Candidate intros drafted" (agent_tool, weekly Friday 17:00, evidence
// required, reminds holder and supervisor) reported last period by a reporting run; "Offers
// accepted" (flagged for review) with the last period's value pending review; the Recruiting
// lead's "Interview debriefs logged" (manual, Monday 10:00 for the previous week) never entered,
// so Late.
import {
  agentJobDueRule,
  coveredPeriodKey,
  type DueRule,
  describeDueRule,
  dutyOrderText,
  dutyStatus,
  lastDue,
  type PeriodType,
} from "@companyos/domain-kpi";
import type { OperationOutput, Problem, RouteId } from "@companyos/sdk";
import type { Handler, Principal } from "./fake";
import { NO_EXECUTION } from "./fake-approval-execution";
import { CATALOG, COMPANY_IDS, type FakeCompany, type FakeDe, metricDefId, uid } from "./fake-data";
import { observationRow, people, type ReportingSeedKey, reportingMetricId } from "./fake-entry";
import { FIRST_ATTEMPT } from "./fake-runs";
import type {
  ApprovalRow,
  MetricDef,
  Observation,
  ScorecardMetric,
  Seat,
  SeatAssignment,
} from "./types";

type ReportingDuty = OperationOutput<"reporting.putDuty">;
type SeatReporting = OperationOutput<"reporting.seat">;
type SeatReportingMetric = SeatReporting["metrics"][number];
type ReportingStatus = NonNullable<ScorecardMetric["reporting"]>;
type PendingReview = NonNullable<ScorecardMetric["pending_review"]>;
type LastReport = NonNullable<ScorecardMetric["last_report"]>;
type KpiScope = "kpis:read" | "kpis:write";
type Run = OperationOutput<"runs.list">["data"][number];
type RunStep = OperationOutput<"runs.get">["steps"][number];
type Provenance = NonNullable<LastReport["provenance"]>;

/** `approval.proposal.change` of a `kpi_value` approval (the contract's `KpiValueChange`). */
export type KpiValueChange = {
  metric_def_id: string;
  metric_key: string;
  metric_name: string;
  seat_id: string;
  seat_title: string;
  kind: "value" | "event";
  value: number | null;
  quantity: number | null;
  unit: string;
  period_type: string;
  period_key: string;
  occurred_at: string | null;
  event_type: string | null;
  provenance: Provenance;
};

const EPOCH = "2026-06-01T09:00:00Z";
const MIN = 60_000;
const BOTH: KpiScope[] = ["kpis:read", "kpis:write"];
const METHODS = ["agent_tool", "manual", "import", "events"] as const;
const WALL = /^([01]\d|2[0-3]):[0-5]\d$/;

const PERIOD_OF: Record<string, PeriodType> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
  quarterly: "quarter",
  yearly: "year",
};

/** The seat measurable's period type (null: no cadence). */
export const periodTypeOfCadence = (cadence: MetricDef["cadence"]): PeriodType | null =>
  cadence ? (PERIOD_OF[cadence] ?? null) : null;

/** How a DE reports the metric (`kpis.read` `report`). */
export const reportOf = (def: Pick<MetricDef, "source">): "value" | "event" | "none" =>
  def.source === "event" ? "event" : def.source === "formula" ? "none" : "value";

// Seed ------------------------------------------------------------------------------------------

/** Riley's seat, its supervisor's (the Recruiting lead, Jordan Lee) and their memberships. */
export const REPORTING_SEATS = {
  riley: uid(0x2000, 0xa01),
  lead: uid(0x2000, 0xa11),
} as const;
const RILEY = uid(0x1000, 0xa01);
const JORDAN = uid(0x1000, 0xa11);

const FRIDAY_5PM: DueRule = { freq: "weekly", weekday: 5, time: "17:00", covers: "current" };
const MONDAY_10AM: DueRule = { freq: "weekly", weekday: 1, time: "10:00", covers: "previous" };

type DutySeed = {
  n: number;
  seat: string;
  key: ReportingSeedKey;
  method: ReportingDuty["method"];
  rule: DueRule;
  lead: number;
  evidence: boolean;
  remind: ReportingDuty["remind"];
};
const DUTY_SEEDS: DutySeed[] = [
  {
    n: 1,
    seat: REPORTING_SEATS.riley,
    key: "de.intros_drafted",
    method: "agent_tool",
    rule: FRIDAY_5PM,
    lead: 60,
    evidence: true,
    remind: ["holder", "supervisor"],
  },
  {
    n: 2,
    seat: REPORTING_SEATS.riley,
    key: "de.offers_accepted",
    method: "agent_tool",
    rule: FRIDAY_5PM,
    lead: 60,
    evidence: true,
    remind: ["holder"],
  },
  {
    n: 3,
    seat: REPORTING_SEATS.lead,
    key: "recruiting.debriefs",
    method: "manual",
    rule: MONDAY_10AM,
    lead: 120,
    evidence: false,
    remind: ["holder", "supervisor"],
  },
];

export const reportingDutyId = (n: number) => uid(0x5d10, n);
/** The two seeded reporting runs (intros recorded; offers held for review). */
export const REPORTING_RUN_IDS = { intros: uid(0x90f0, 1), offers: uid(0x90f0, 2) } as const;
export const KPI_APPROVAL_ID = uid(0x98f0, 1);

type SeatMetricSeed = {
  metric_def_id: string;
  kpi_weight: string;
  goal_weight: string;
  tracking_mode: "off" | "context" | "scored";
  penalty_only: boolean;
  position: number;
};

/**
 * The seat metric sets the twin starts with: Riley's DE catalog plus the two reported metrics,
 * and the Recruiting lead's debriefs (a person-held seat with a scored metric joins the roll-up).
 */
export function seededSeatMetricSets(): [string, SeatMetricSeed[]][] {
  const co = COMPANY_IDS.acme;
  const item = (id: string, kpi: number, goal: number, mode: SeatMetricSeed["tracking_mode"]) => ({
    metric_def_id: id,
    kpi_weight: String(kpi),
    goal_weight: String(goal),
    tracking_mode: mode,
    penalty_only: false,
  });
  const riley = [
    ...CATALOG.map((m) =>
      item(metricDefId(co, m.key), m.kpi_weight, m.goal_weight, m.tracking_mode),
    ),
    item(reportingMetricId("de.intros_drafted"), 1, 0, "scored"),
    item(reportingMetricId("de.offers_accepted"), 1, 1, "scored"),
  ].map((m, position) => ({ ...m, position }));
  const lead = [{ ...item(reportingMetricId("recruiting.debriefs"), 1, 0, "scored"), position: 0 }];
  return [
    [REPORTING_SEATS.riley, riley],
    [REPORTING_SEATS.lead, lead],
  ];
}

const acmeCfg = (company: FakeCompany) => company.calendar_config;

function seededDuties(company: FakeCompany): ReportingDuty[] {
  if (company.id !== COMPANY_IDS.acme) return [];
  return DUTY_SEEDS.map((s) => ({
    company_id: company.id,
    id: reportingDutyId(s.n),
    seat_id: s.seat,
    metric_def_id: reportingMetricId(s.key),
    method: s.method,
    due_rule: s.rule,
    lead_minutes: s.lead,
    evidence_required: s.evidence,
    remind: s.remind,
    actor_kind: "person",
    actor_membership_id: null,
    created_at: EPOCH,
    updated_at: EPOCH,
  }));
}

/** The latest Friday 17:00 due instant and the period it covers (both seeded reports are for it). */
function lastFriday(company: FakeCompany, now: Date): { due: Date; period: string } {
  const cfg = acmeCfg(company);
  const due = lastDue(FRIDAY_5PM, cfg.timezone, now);
  return { due, period: coveredPeriodKey(cfg, "week", FRIDAY_5PM, due) };
}

const INTROS_EVIDENCE: Provenance = {
  run_id: REPORTING_RUN_IDS.intros,
  agent_membership_id: RILEY,
  agent_name: "Riley",
  evidence_text: "14 candidate intros drafted for the Senior AE and Finance Manager roles",
  evidence_url: "https://mail.acme.example/drafts?label=candidate-intros",
};

/** Riley's intros reported last period (agent, with provenance) and a person's older numbers. */
export function seedReportingObservations(companies: FakeCompany[], now: Date): Observation[] {
  const company = companies.find((c) => c.id === COMPANY_IDS.acme);
  if (!company) return [];
  const { due, period } = lastFriday(company, now);
  const cfg = acmeCfg(company);
  const before = coveredPeriodKey(
    cfg,
    "week",
    FRIDAY_5PM,
    new Date(due.getTime() - 7 * 86_400_000),
  );
  const at = (ms: number) => new Date(ms).toISOString();
  const seat = { kind: "seat" as const, id: REPORTING_SEATS.riley };
  const intros = observationRow(company.id, 0xd01, {
    metric_def_id: reportingMetricId("de.intros_drafted"),
    period_type: "week",
    period_key: period,
    value: 14,
    note: null,
    subject: seat,
    created_at: at(due.getTime() - 50 * MIN),
  });
  const olderIntros = observationRow(company.id, 0xd02, {
    metric_def_id: reportingMetricId("de.intros_drafted"),
    period_type: "week",
    period_key: before,
    value: 11,
    note: "Entered by Jordan before the duty moved to Riley",
    subject: seat,
    created_at: at(due.getTime() - 7 * 86_400_000),
  });
  const offers = observationRow(company.id, 0xd03, {
    metric_def_id: reportingMetricId("de.offers_accepted"),
    period_type: "week",
    period_key: before,
    value: 1,
    note: "One offer signed (entered by Jordan)",
    subject: seat,
    created_at: at(due.getTime() - 7 * 86_400_000 + 30 * MIN),
  });
  return [
    {
      ...intros,
      source: "agent",
      source_event_id: `run:${REPORTING_RUN_IDS.intros}:1`,
      actor_kind: "digital_employee",
      actor_membership_id: RILEY,
      provenance: INTROS_EVIDENCE,
    },
    { ...olderIntros, actor_kind: "person", actor_membership_id: JORDAN },
    { ...offers, actor_kind: "person", actor_membership_id: JORDAN },
  ];
}

/**
 * The Agents twin's reporting runs and the review they raised (fake-agents.ts `seedMore`): Riley
 * woken by its intros duty (recorded) and by its offers duty (held as a `kpi_value` approval).
 */
export function seedReportingRuns(
  company: FakeCompany,
  des: FakeDe[],
  now: Date,
): { runs: Run[]; steps: [string, RunStep[]][]; approvals: ApprovalRow[] } {
  const riley = des.find((d) => d.membership_id === RILEY);
  if (company.id !== COMPANY_IDS.acme || !riley) return { runs: [], steps: [], approvals: [] };
  const { due, period } = lastFriday(company, now);
  const iso = (ms: number) => new Date(ms).toISOString();
  const offersValue = 3;
  const offersEvidence: Provenance = {
    run_id: REPORTING_RUN_IDS.offers,
    agent_membership_id: RILEY,
    agent_name: "Riley",
    evidence_text: "Three offers signed in Greenhouse: A. Ruiz, B. Okafor and C. Li",
    evidence_url: `https://acme.greenhouse.example/reports/offers?week=${period}`,
  };
  const plans = [
    {
      id: REPORTING_RUN_IDS.intros,
      duty: reportingDutyId(1),
      start: due.getTime() - 60 * MIN,
      key: "de.intros_drafted",
      name: "Candidate intros drafted",
      value: 14,
      status: "recorded",
      evidence: INTROS_EVIDENCE,
      summary: `Reported 14 candidate intros drafted for ${period}.`,
    },
    {
      id: REPORTING_RUN_IDS.offers,
      duty: reportingDutyId(2),
      start: due.getTime() - 58 * MIN,
      key: "de.offers_accepted",
      name: "Offers accepted",
      value: offersValue,
      status: "pending_review",
      evidence: offersEvidence,
      summary: `Reported ${offersValue} offers accepted for ${period}; it waits for Jordan's review.`,
    },
  ];
  const runs: Run[] = [];
  const steps: [string, RunStep[]][] = [];
  plans.forEach((p, i) => {
    const step = (
      k: number,
      kind: RunStep["kind"],
      payload: Record<string, unknown>,
      cost = 0,
    ) => ({
      company_id: company.id,
      id: uid(0x92f0, (i + 1) * 16 + k),
      run_id: p.id,
      seq: k,
      kind,
      payload,
      cost_micros: cost,
      tokens_in: kind === "model_call" ? 2_400 : 0,
      tokens_out: kind === "model_call" ? 180 : 0,
      model: kind === "model_call" ? "claude-sonnet" : null,
      duration_ms: kind === "model_call" ? 2_100 : 400,
      created_at: iso(p.start + 2_000 + k * 1_500),
    });
    const list: RunStep[] = [
      step(
        1,
        "model_call",
        {
          summary: `Woken by the reporting duty: ${p.name} is due Friday 17:00`,
        },
        12_000,
      ),
      step(2, "tool_call", {
        tool: "kpis.read",
        summary: "Read Your KPIs and the reporting orders",
      }),
      step(3, "tool_call", {
        tool: "outcomes.record",
        metric_key: p.key,
        kind: "value",
        value: p.value,
        period_key: period,
        status: p.status,
        evidence_text: p.evidence.evidence_text,
      }),
    ];
    steps.push([p.id, list]);
    runs.push({
      company_id: company.id,
      id: p.id,
      membership_id: riley.membership_id,
      crew_agent_id: null,
      seat_id: riley.seat_id,
      agent_profile_id: null,
      owner_membership_id: null,
      identity_mode: "as_agent",
      trigger_kind: "reporting",
      trigger_ref: { kind: "reporting_duty", id: p.duty },
      wakeup_ids: [uid(0x95f0, i + 1)],
      state: "completed",
      state_reason: null,
      started_at: iso(p.start + 1_000),
      ended_at: iso(p.start + 9_000),
      parent_run_id: null,
      delegation_depth: 0,
      budget_reserved_micros: 0,
      cost_micros: 12_000,
      tokens_in: 2_400,
      tokens_out: 180,
      model: "claude-sonnet",
      sandbox_kind: null,
      skill_pack_versions: { recruiting: "1.4.0" },
      summary: p.summary,
      hand_back: { summary: p.summary, needs: "nothing" },
      lease_owner: null,
      lease_until: null,
      verdict: "verified",
      verdict_evidence_ref: null,
      output_ref: null,
      model_path: "gateway",
      model_path_fallback_used: false,
      model_credential_id: null,
      context_refs: null,
      bundle_sha: null,
      ...FIRST_ATTEMPT,
      task_id: null,
      actor_kind: "system",
      actor_membership_id: null,
      created_at: iso(p.start),
      updated_at: iso(p.start + 9_000),
    });
  });
  const offers = plans[1];
  if (!offers) return { runs, steps, approvals: [] };
  const change: KpiValueChange = {
    metric_def_id: reportingMetricId("de.offers_accepted"),
    metric_key: "de.offers_accepted",
    metric_name: "Offers accepted",
    seat_id: riley.seat_id,
    seat_title: riley.seat_title,
    kind: "value",
    value: offersValue,
    quantity: null,
    unit: "number",
    period_type: "week",
    period_key: period,
    occurred_at: null,
    event_type: null,
    provenance: offersEvidence,
  };
  const at = iso(offers.start + 7_000);
  const approval: ApprovalRow = {
    company_id: company.id,
    id: KPI_APPROVAL_ID,
    kind: "kpi_value",
    subject_kind: "metric_def",
    subject_id: change.metric_def_id,
    requested_by_seat_id: riley.seat_id,
    requested_by_membership_id: riley.membership_id,
    approver_seat_id: riley.supervisor.seat_id,
    run_id: offers.id,
    proposal: {
      title: `Offers accepted: ${offersValue} for ${period}`,
      summary: offersEvidence.evidence_text,
      change,
    },
    state: "pending",
    approved_payload: null,
    decided_at: null,
    decided_by_membership_id: null,
    reason: null,
    thread_id: null,
    expires_at: null,
    executed_at: null,
    ...NO_EXECUTION,
    actor_kind: "digital_employee",
    actor_membership_id: riley.membership_id,
    created_at: at,
    updated_at: at,
  };
  return { runs, steps, approvals: [approval] };
}

/**
 * An edit of a `kpi_value` approval may change `value` (kind value) or `quantity` (kind event)
 * only, to a number (13 §Reporting duties). Null when the edit is acceptable.
 */
export function kpiEditProblem(before: unknown, after: unknown): string | null {
  if (typeof after !== "object" || after === null) return "proposal.change: an object";
  const a = before as Record<string, unknown>;
  const b = after as Record<string, unknown>;
  const field = a.kind === "event" ? "quantity" : "value";
  if (typeof b[field] !== "number" || !Number.isFinite(b[field]))
    return `proposal.change.${field}: a number`;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if (k === field) continue;
    if (JSON.stringify(a[k]) !== JSON.stringify(b[k]))
      return `proposal.change.${k}: only ${field} may be edited`;
  }
  return null;
}

// Handlers --------------------------------------------------------------------------------------

export type ReportingStores = {
  seatsOf: (company: FakeCompany) => Seat[];
  metricsOf: (company: FakeCompany) => MetricDef[];
  assignmentsOf: (company: FakeCompany) => SeatAssignment[];
  /** The seat's measured metrics (its metric set; a DE seat without one: the DE catalog). */
  measuredOf: (
    company: FakeCompany,
    seatId: string,
  ) => { metric_def_id: string; tracking_mode: SeatReportingMetric["tracking_mode"] }[];
};

export type ReportingDeps = {
  json: (body: unknown, status?: number) => Response;
  problem: (status: number, code: Problem["code"], title: string, detail?: string) => Response;
  notFound: () => Response;
  validation: (detail: string) => Response;
  now: () => Date;
  nextSeq: () => number;
  desOf: (companyId: string) => FakeDe[];
  stores: () => ReportingStores | null;
  /** The shared observation rows (scorecards read the reviewed values back). */
  observations: Observation[];
  /** The Agents twin's approvals (the pending `kpi_value` ones are the values under review). */
  approvalsOf: (companyId: string) => readonly ApprovalRow[];
};

export type ReportingFake = {
  handlers: Partial<Record<RouteId, Handler>>;
  /** What a seat scorecard row carries for the metric (`reporting`, `pending_review`, `last_report`). */
  metricExtras: (
    p: Principal,
    seatId: string,
    def: MetricDef,
  ) => Pick<ScorecardMetric, "reporting" | "pending_review" | "last_report">;
  /** Records the value of a decided `kpi_value` approval (approved or edited). */
  recordReview: (company: FakeCompany, approval: ApprovalRow) => void;
  /** DE-WIZARD: the duties a DE's job sets and removes (fake-de-job.ts). */
  agentDuty: AgentJobDuty;
};

/**
 * DE-WIZARD ↔ AGENT-REPORTING (routes/org/de-job.ts): a KPI the job marks "reported by the
 * agent" sets the seat's `agent_tool` duty (domain-kpi `agentJobDueRule`, lead 60, reminding the
 * holder); one that leaves the job's reporting removes the duty only while it is `agent_tool`.
 */
export type AgentJobDuty = {
  set: (
    company: FakeCompany,
    seatId: string,
    metricId: string,
    duty: "daily" | "weekly" | "monthly",
  ) => void;
  remove: (company: FakeCompany, seatId: string, metricId: string) => void;
};

export function createReportingFake(d: ReportingDeps): ReportingFake {
  const { json, problem, notFound, validation } = d;
  const forbidden = (detail = "managers only") => problem(403, "forbidden", "Forbidden", detail);
  const duties = new Map<string, ReportingDuty[]>();

  function dutiesOf(company: FakeCompany): ReportingDuty[] {
    let list = duties.get(company.id);
    if (!list) {
      list = seededDuties(company);
      duties.set(company.id, list);
    }
    return list;
  }

  const dutyOf = (company: FakeCompany, seatId: string, metricId: string) =>
    dutiesOf(company).find((x) => x.seat_id === seatId && x.metric_def_id === metricId) ?? null;

  function holderOf(company: FakeCompany, seatId: string): SeatReporting["holder"] {
    const stores = d.stores();
    const a = stores
      ?.assignmentsOf(company)
      .find((x) => x.seat_id === seatId && x.active_to === null);
    if (!a) return null;
    const des = d.desOf(company.id);
    if (a.membership_kind === "digital_employee") {
      const de = des.find((x) => x.membership_id === a.membership_id);
      return {
        membership_id: a.membership_id,
        kind: "digital_employee",
        name: de?.display_name ?? "Digital employee",
      };
    }
    const person = people(company, des, true).find((x) => x.id === a.membership_id);
    return {
      membership_id: a.membership_id,
      kind: "person",
      name: person?.display_name ?? "A person",
    };
  }

  function scopesOf(seat: Seat): { kpi_scopes: KpiScope[] | null; effective: KpiScope[] } {
    const raw = (seat.settings as Record<string, unknown> | null)?.kpi_scopes;
    const named = Array.isArray(raw) ? (raw.filter((s) => BOTH.includes(s)) as KpiScope[]) : null;
    return { kpi_scopes: named, effective: named ?? BOTH };
  }

  function rowsOf(company: FakeCompany, seatId: string, metricId: string, type: PeriodType) {
    return d.observations.filter(
      (o) =>
        o.company_id === company.id &&
        o.metric_def_id === metricId &&
        o.subject_kind === "seat" &&
        o.subject_id === seatId &&
        o.period_type === type,
    );
  }

  /** Each period's live row (newest non-tombstoned, each tombstone cancelling the next older). */
  function liveByPeriod(rows: Observation[]): Map<string, Observation> {
    const out = new Map<string, Observation>();
    const keys = new Set(rows.map((r) => r.period_key));
    for (const k of keys) {
      const chain = rows
        .filter((r) => r.period_key === k)
        .sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id));
      let pending = 0;
      for (const r of chain) {
        if (r.tombstone) pending += 1;
        else if (pending > 0) pending -= 1;
        else {
          if (r.value !== null) out.set(k, r);
          break;
        }
      }
    }
    return out;
  }

  const pendingOf = (company: FakeCompany, seatId: string, metricId: string) =>
    d
      .approvalsOf(company.id)
      .filter((a) => a.kind === "kpi_value" && a.state === "pending")
      .filter((a) => {
        const c = a.proposal.change as Partial<KpiValueChange>;
        return c.seat_id === seatId && c.metric_def_id === metricId;
      })
      .sort((a, b) => b.created_at.localeCompare(a.created_at));

  function pendingReview(
    company: FakeCompany,
    seatId: string,
    metricId: string,
  ): PendingReview | null {
    const list = pendingOf(company, seatId, metricId);
    const newest = list[0];
    if (!newest) return null;
    const c = newest.proposal.change as KpiValueChange;
    return {
      approval_id: newest.id,
      run_id: newest.run_id,
      period_key: c.period_key,
      value: c.value,
      quantity: c.quantity,
      requested_at: newest.created_at,
      count: list.length,
    };
  }

  function lastReport(company: FakeCompany, seatId: string, def: MetricDef): LastReport | null {
    const type = periodTypeOfCadence(def.cadence);
    if (!type) return null;
    const live = [...liveByPeriod(rowsOf(company, seatId, def.id, type)).values()].sort((a, b) =>
      b.created_at.localeCompare(a.created_at),
    )[0];
    if (!live || live.value === null) return null;
    return {
      period_key: live.period_key,
      value: Number(live.value),
      source: live.source,
      recorded_at: live.created_at,
      provenance: (live.provenance as Provenance | null) ?? null,
    };
  }

  function blockedReason(
    company: FakeCompany,
    seat: Seat,
    def: MetricDef,
    duty: ReportingDuty,
  ): string | null {
    const holder = holderOf(company, seat.id);
    if (seat.status === "open" || !holder) return "The seat is open: nobody holds it to report.";
    if (
      !d
        .stores()
        ?.measuredOf(company, seat.id)
        .some((m) => m.metric_def_id === def.id)
    )
      return "The metric is not measured on this seat.";
    if (!periodTypeOfCadence(def.cadence)) return "The metric has no cadence, so nothing is due.";
    switch (duty.method) {
      case "agent_tool":
        if (holder.kind !== "digital_employee")
          return "Agent tool needs a digital employee holding the seat.";
        if (def.source === "formula")
          return "The metric is derived from other numbers; nothing to report.";
        if (!scopesOf(seat).effective.includes("kpis:write"))
          return "The seat's agent may not record KPIs (kpis:write is off).";
        return null;
      case "manual":
      case "import":
        if (def.source === "event" || def.source === "formula")
          return `The metric is ${def.source === "event" ? "counted from events" : "derived from other numbers"}; it is not entered by hand.`;
        return null;
      case "events":
        return def.source === "event" ? null : "The metric is not counted from events.";
    }
  }

  function statusOf(
    company: FakeCompany,
    seat: Seat,
    def: MetricDef,
    duty: ReportingDuty,
  ): ReportingStatus {
    const type = periodTypeOfCadence(def.cadence) ?? "week";
    const reported = new Set(liveByPeriod(rowsOf(company, seat.id, def.id, type)).keys());
    for (const a of pendingOf(company, seat.id, def.id))
      reported.add((a.proposal.change as KpiValueChange).period_key);
    const rule = duty.due_rule as DueRule;
    const s = dutyStatus({
      rule,
      cfg: company.calendar_config,
      periodType: type,
      leadMinutes: duty.lead_minutes,
      since: new Date(duty.created_at),
      reported,
      blockedReason: blockedReason(company, seat, def, duty),
      now: d.now(),
    });
    return {
      duty_id: duty.id,
      method: duty.method,
      due_rule: duty.due_rule,
      due_label: describeDueRule(rule, type),
      evidence_required: duty.evidence_required,
      state: s.state,
      next_due_at: s.next_due_at.toISOString(),
      next_period_key: s.next_period_key,
      last_due_at: s.last_due_at ? s.last_due_at.toISOString() : null,
      last_period_key: s.last_period_key,
      late_since: s.late_since ? s.late_since.toISOString() : null,
      blocked_reason: s.blocked_reason,
    };
  }

  function metricRow(
    company: FakeCompany,
    seat: Seat,
    def: MetricDef,
    mode: SeatReportingMetric["tracking_mode"],
  ): SeatReportingMetric {
    const duty = dutyOf(company, seat.id, def.id);
    const holder = holderOf(company, seat.id);
    const type = periodTypeOfCadence(def.cadence);
    return {
      metric_def_id: def.id,
      key: def.key,
      name: def.name,
      source: def.source,
      cadence: def.cadence,
      tracking_mode: mode,
      needs_review: def.needs_review,
      report: reportOf(def),
      duty,
      status: duty ? statusOf(company, seat, def, duty) : null,
      order_text:
        duty && holder?.kind === "digital_employee" && type
          ? dutyOrderText({
              metricName: def.name,
              metricKey: def.key,
              method: duty.method,
              rule: duty.due_rule as DueRule,
              timezone: company.calendar_config.timezone,
              periodType: type,
              report: reportOf(def),
              evidenceRequired: duty.evidence_required,
            })
          : null,
      last_report: lastReport(company, seat.id, def),
      pending_review: pendingReview(company, seat.id, def.id),
    };
  }

  const seatOf = (p: Principal, id: string | undefined) =>
    d
      .stores()
      ?.seatsOf(p.company)
      .find((s) => s.id === id) ?? null;
  /** `seat#can_view`: a manager, the seat's holder, or a holder of its supervisor seat. */
  function canViewSeat(p: Principal, seat: Seat): boolean {
    if (p.manager) return true;
    const mine = new Set(
      p.me.memberships.filter((m) => m.company_id === p.company.id).map((m) => m.membership_id),
    );
    return (d.stores()?.assignmentsOf(p.company) ?? []).some(
      (a) =>
        a.active_to === null &&
        mine.has(a.membership_id) &&
        (a.seat_id === seat.id || a.seat_id === seat.supervisor_seat_id),
    );
  }
  const defOf = (p: Principal, id: string | undefined) =>
    d
      .stores()
      ?.metricsOf(p.company)
      .find((m) => m.id === id && (m.visibility === "client" || p.operator)) ?? null;

  function checkDuty(b: Record<string, unknown>): string | null {
    const allowed = ["method", "due_rule", "lead_minutes", "evidence_required", "remind"];
    const extra = Object.keys(b).find((k) => !allowed.includes(k));
    if (extra) return `${extra}: not a duty field`;
    if (!METHODS.includes(b.method as (typeof METHODS)[number]))
      return `method: one of ${METHODS.join(", ")}`;
    const r = b.due_rule as Record<string, unknown> | undefined;
    if (!r || typeof r !== "object") return "due_rule: required";
    if (typeof r.time !== "string" || !WALL.test(r.time)) return "due_rule.time: HH:MM";
    if (r.covers !== undefined && r.covers !== "current" && r.covers !== "previous")
      return "due_rule.covers: current or previous";
    const keys: Record<string, string[]> = {
      daily: ["freq", "time", "covers"],
      weekly: ["freq", "weekday", "time", "covers"],
      monthly: ["freq", "day", "time", "covers"],
    };
    const known = keys[String(r.freq)];
    if (!known) return "due_rule.freq: daily, weekly or monthly";
    const stray = Object.keys(r).find((k) => !known.includes(k));
    if (stray) return `due_rule.${stray}: not part of a ${String(r.freq)} rule`;
    if (
      r.freq === "weekly" &&
      !(Number.isInteger(r.weekday) && (r.weekday as number) >= 1 && (r.weekday as number) <= 7)
    )
      return "due_rule.weekday: 1 (Monday) to 7 (Sunday)";
    if (
      r.freq === "monthly" &&
      !(Number.isInteger(r.day) && (r.day as number) >= 1 && (r.day as number) <= 31)
    )
      return "due_rule.day: 1 to 31";
    if (
      b.lead_minutes !== undefined &&
      !(
        Number.isInteger(b.lead_minutes) &&
        (b.lead_minutes as number) >= 0 &&
        (b.lead_minutes as number) <= 1440
      )
    )
      return "lead_minutes: 0 to 1440";
    if (b.evidence_required !== undefined && typeof b.evidence_required !== "boolean")
      return "evidence_required: a boolean";
    if (b.remind !== undefined) {
      if (!Array.isArray(b.remind) || b.remind.length > 2)
        return "remind: holder and/or supervisor";
      if (b.remind.some((x) => x !== "holder" && x !== "supervisor"))
        return "remind: holder and/or supervisor";
      if (new Set(b.remind).size !== b.remind.length) return "remind: no repeats";
    }
    return null;
  }

  const handlers: Partial<Record<RouteId, Handler>> = {
    "reporting.seat": ({ principal: p, params }) => {
      if (!p) return notFound();
      const seat = seatOf(p, params.id);
      if (!seat) return notFound();
      // `seat#can_view` (auth/can.ts): a client viewer has no seat grant, so, as anyone who may
      // not see the seat, gets 404 (the Setup screen is not a client view).
      if (!canViewSeat(p, seat)) return notFound();
      const stores = d.stores();
      const defs = new Map((stores?.metricsOf(p.company) ?? []).map((m) => [m.id, m]));
      const set = stores?.measuredOf(p.company, seat.id) ?? [];
      const metrics = set.flatMap((m) => {
        const def = defs.get(m.metric_def_id);
        if (!def || def.archived_at || (def.visibility !== "client" && !p.operator)) return [];
        return [metricRow(p.company, seat, def, m.tracking_mode)];
      });
      const body: SeatReporting = {
        seat: { id: seat.id, title: seat.title, supervisor_seat_id: seat.supervisor_seat_id },
        holder: holderOf(p.company, seat.id),
        timezone: p.company.calendar_config.timezone,
        kpi_scopes: { seat_id: seat.id, ...scopesOf(seat) },
        can_edit: p.manager,
        metrics,
        as_of: d.now().toISOString(),
      };
      return json(body);
    },
    "reporting.putDuty": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden("a manager or the seat's supervisor");
      const seat = seatOf(p, params.id);
      const def = defOf(p, params.metric_def_id);
      if (!seat || !def) return notFound();
      if (seat.status === "closed") return problem(409, "seat_closed", "Seat is closed");
      const b = (body ?? {}) as Record<string, unknown>;
      const bad = checkDuty(b);
      if (bad) return validation(bad);
      const at = d.now().toISOString();
      const list = dutiesOf(p.company);
      const existing = dutyOf(p.company, seat.id, def.id);
      const rule = { ...(b.due_rule as DueRule) };
      const next: ReportingDuty = {
        company_id: p.company.id,
        id: existing?.id ?? uid(0x5d20, d.nextSeq()),
        seat_id: seat.id,
        metric_def_id: def.id,
        method: b.method as ReportingDuty["method"],
        due_rule: rule,
        lead_minutes: (b.lead_minutes as number | undefined) ?? 60,
        evidence_required: (b.evidence_required as boolean | undefined) ?? false,
        remind: (b.remind as ReportingDuty["remind"] | undefined) ?? ["holder"],
        actor_kind: p.operator ? "agency_user" : "person",
        actor_membership_id: null,
        created_at: existing?.created_at ?? at,
        updated_at: at,
      };
      if (existing) list.splice(list.indexOf(existing), 1, next);
      else list.push(next);
      return json(next);
    },
    "reporting.deleteDuty": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden("a manager or the seat's supervisor");
      const seat = seatOf(p, params.id);
      if (!seat || !defOf(p, params.metric_def_id)) return notFound();
      const list = dutiesOf(p.company);
      const existing = dutyOf(p.company, seat.id, params.metric_def_id ?? "");
      if (existing) list.splice(list.indexOf(existing), 1);
      return new Response(null, { status: 204 });
    },
    "seats.putKpiScopes": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden("a manager or the seat's supervisor");
      const seat = seatOf(p, params.id);
      if (!seat) return notFound();
      if (seat.status === "closed") return problem(409, "seat_closed", "Seat is closed");
      const b = (body ?? {}) as Record<string, unknown>;
      const extra = Object.keys(b).find((k) => k !== "kpi_scopes");
      if (extra) return validation(`${extra}: not a field`);
      if (!("kpi_scopes" in b)) return validation("kpi_scopes: required (null for the default)");
      const v = b.kpi_scopes;
      if (v !== null) {
        if (!Array.isArray(v) || v.length > 2 || v.some((s) => !BOTH.includes(s)))
          return validation("kpi_scopes: kpis:read and/or kpis:write, or null");
        if (new Set(v).size !== v.length) return validation("kpi_scopes: no repeats");
      }
      const settings = { ...((seat.settings as Record<string, unknown> | null) ?? {}) };
      if (v === null) delete settings.kpi_scopes;
      else settings.kpi_scopes = v;
      Object.assign(seat, {
        settings,
        version: seat.version + 1,
        updated_at: d.now().toISOString(),
      });
      return json({ seat_id: seat.id, ...scopesOf(seat) });
    },
  };

  const agentDuty: AgentJobDuty = {
    set(company, seatId, metricId, duty) {
      const list = dutiesOf(company);
      const existing = dutyOf(company, seatId, metricId);
      const at = d.now().toISOString();
      const next: ReportingDuty = {
        company_id: company.id,
        id: existing?.id ?? uid(0x5d20, d.nextSeq()),
        seat_id: seatId,
        metric_def_id: metricId,
        method: "agent_tool",
        due_rule: agentJobDueRule(duty),
        lead_minutes: 60,
        evidence_required: false,
        remind: ["holder"],
        actor_kind: "person",
        actor_membership_id: null,
        created_at: existing?.created_at ?? at,
        updated_at: at,
      };
      if (existing) list.splice(list.indexOf(existing), 1, next);
      else list.push(next);
    },
    remove(company, seatId, metricId) {
      const list = dutiesOf(company);
      const existing = dutyOf(company, seatId, metricId);
      if (existing?.method === "agent_tool") list.splice(list.indexOf(existing), 1);
    },
  };

  return {
    handlers,
    agentDuty,
    metricExtras(p, seatId, def) {
      const seat = seatOf(p, seatId);
      const duty = seat ? dutyOf(p.company, seatId, def.id) : null;
      const pending = pendingReview(p.company, seatId, def.id);
      const last = lastReport(p.company, seatId, def);
      return {
        ...(seat && duty ? { reporting: statusOf(p.company, seat, def, duty) } : {}),
        ...(pending ? { pending_review: pending } : {}),
        ...(last ? { last_report: last } : {}),
      };
    },
    recordReview(company, a) {
      if (a.kind !== "kpi_value" || (a.state !== "approved" && a.state !== "edited")) return;
      const asked = a.proposal.change as KpiValueChange;
      const final = (a.approved_payload?.change ?? asked) as KpiValueChange;
      const value = final.kind === "event" ? final.quantity : final.value;
      if (value === null || value === undefined) return;
      const supervisor = d.desOf(company.id).find((x) => x.seat_id === final.seat_id)?.supervisor;
      const row = observationRow(company.id, d.nextSeq(), {
        metric_def_id: final.metric_def_id,
        period_type: final.period_type as Observation["period_type"],
        period_key: final.period_key,
        value,
        note: null,
        subject: { kind: "seat", id: final.seat_id },
        created_at: d.now().toISOString(),
      });
      d.observations.push({
        ...row,
        source: "agent",
        source_event_id: `review:${a.id}`,
        actor_kind: "person",
        actor_membership_id: a.decided_by_membership_id,
        provenance: {
          ...final.provenance,
          review: {
            approval_id: a.id,
            state: a.state === "edited" ? "edited" : "approved",
            decided_by_membership_id:
              a.decided_by_membership_id ?? supervisor?.membership_id ?? JORDAN,
            original_value: asked.kind === "event" ? asked.quantity : asked.value,
          },
        },
      });
    },
  };
}
